import { html, unsafeCSS } from 'lit';
import { property, query } from 'lit/decorators.js';
import { DomUtility, SvgUtility } from '~/ui/utils/index.js';

import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { DERStatus } from '~/ui/@types/der-status';
import { styles as globalStyles } from '~/ui/scss/main.styles';
import DERFormElement from '~/ui/utils/der-form-element';
import { styles } from './input.styles';

export class Input extends DERFormElement {
    static override styles = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @query('input')
    inputEl!: HTMLInputElement;

    @property({ type: String })
    inputId: string = DomUtility.uniqueElementId(this);

    @property({ type: Array })
    datalistOptions: string[] = [];

    @property({ type: Boolean })
    disabled: boolean = false;

    @property({ type: String })
    label?: string;

    @property({ type: String, attribute: 'optional-label' })
    optionalLabel? = '(Optional)';

    @property({ type: Number })
    maxlength?: number;

    @property({ type: Number })
    minlength?: number;

    @property({ type: Number })
    min?: number;

    @property({ type: Number })
    max?: number;

    @property({ type: String })
    placeholder?: string;

    @property({ type: String })
    message?: string;

    @property({ type: Boolean })
    readonly = false;

    @property({ type: Boolean })
    required = false;

    @property({ type: Boolean })
    combined = false;

    @property({ type: String, reflect: true })
    value: string = '';

    @property({ type: String })
    type:
        | 'hidden'
        | 'text'
        | 'search'
        | 'tel'
        | 'url'
        | 'email'
        | 'password'
        | 'datetime'
        | 'date'
        | 'month'
        | 'week'
        | 'time'
        | 'datetime-local'
        | 'number'
        | 'range'
        | 'color'
        | 'checkbox'
        | 'radio'
        | 'file'
        | 'submit'
        | 'image'
        | 'reset'
        | 'button' = 'text';

    @property({ type: String })
    name: string = this.inputId;

    @property({ type: String })
    inputmode?: 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url';

    @property({ type: String })
    pattern?: string;

    @property({ type: String })
    status?: DERStatus;

    constructor() {
        super();
        this.attachShadow({
            mode: 'open',
            delegatesFocus: true,
        });
    }

    override connectedCallback() {
        super.connectedCallback();
        this._internals.setFormValue(this.getValue());
        this.updateComplete.then(() => {
            this.checkInputValidity();
            this.syncInputWithValue();
        });
    }

    override updated(changedProperties: Map<string, never>) {
        if (!changedProperties.size) {
            return;
        }

        if (changedProperties.has('value')) {
            this._internals.setFormValue(this.getValue());
            this.syncInputWithValue();
        }
        this.checkInputValidity();
    }

    private syncInputWithValue() {
        if (this.inputEl && this.inputEl.value !== this.value) {
            this.inputEl.value = this.getValue();
        }
    }
    private getValue(): string {
        return this.value?.toString() || '';
    }

    private hasMessage(): boolean {
        return typeof this.message !== 'undefined' && this.message.length > 0;
    }

    private renderDatalist() {
        if (this.datalistOptions && this.datalistOptions.length > 0) {
            return html`
                <datalist id="${this.inputId}-datalist">
                    ${this.datalistOptions.map((option) => html` <option value="${option}">${option}</option>`)}
                </datalist>
            `;
        }

        return null;
    }

    onInput(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.value = target.value;
        this._internals.setFormValue(this.value);
        this.checkInputValidity();
        this.emitEvent('der.input', { value: this.value, event });
    }

    onChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.value = target.value;
        this._internals.setFormValue(this.value);
        this.checkInputValidity();
        this.emitEvent('der.change', { value: this.value, event });
    }

    onKeyUp(event: KeyboardEvent) {
        event.preventDefault();

        if (event.key === 'Enter') {
            this.emitEvent('der.submit', { value: this.value, event });
        }
    }

    private checkInputValidity() {
        if (!this.inputEl) {
            return;
        }

        // If we have a pattern mismatch, make sure it's reflected in the form validity
        this.setDERValidity(this.inputEl.validity, this.inputEl.validationMessage, this.inputEl);

        // If pattern validation fails, also update the status to error and emit an invalid event
        if (this.inputEl.validity.valid) {
            this.requestUpdate();
        }
    }

    override render() {
        const value = this.getValue();
        const { disabled, inputId, name, type, label, optionalLabel, readonly, status, required, placeholder } = this;
        return html`
            <div
                class=${classMap({
                    'c-input__wrapper': true,
                    input: true,
                    disabled: disabled,
                    error: status === 'error',
                    success: status === 'success',
                })}
            >
                ${label
                    ? html` <div class="c-input__label-wrapper">
                          ${label ? html`<label class="c-input__label">${label}</label>` : ''}
                          ${!required && !disabled
                              ? html` <span class="c-input__optional-label">${optionalLabel}</span>`
                              : ''}
                      </div>`
                    : ''}
                <span class="c-input__group">
                    <input
                        class=${classMap({
                            'c-input': true,
                            combined: this.combined,
                        })}
                        type=${type}
                        .value=${value}
                        ?disabled=${disabled}
                        ?readonly=${readonly}
                        ?required=${required}
                        placeholder=${ifDefined(placeholder)}
                        inputmode=${this.inputmode || 'text'}
                        id=${inputId}
                        name=${name}
                        minlength=${ifDefined(this.minlength)}
                        maxlength=${ifDefined(this.maxlength)}
                        min=${ifDefined(this.min)}
                        max=${ifDefined(this.max)}
                        pattern=${ifDefined(this.pattern)}
                        @keyup="${this.onKeyUp}"
                        @input="${this.onInput}"
                        @change="${this.onChange}"
                        @invalid="${(e: Event) => {
                            e.preventDefault(); // Prevent default validation UI
                            this.checkInputValidity();
                        }}"
                    />
                    ${this.renderDatalist()}
                </span>
                ${this.hasMessage()
                    ? html`
                          <p class="c-input__message">
                              <span class="c-input__icon">
                                  <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 40 40"
                                      fill="none"
                                  >
                                      ${SvgUtility.systemAttentionFull}
                                  </svg>
                              </span>
                              ${this.message}
                          </p>
                      `
                    : ''}
            </div>
        `;
    }
}
